import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { useLocalizedPath } from "../components/Header";
import { Star, ChevronLeft, ChevronRight, Quote, CheckCircle2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function StarDisplay({ rating, size = "h-4 w-4" }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`${size} ${
            star <= rating
              ? "text-orange-500 fill-orange-500"
              : star - 0.5 <= rating
              ? "text-orange-500 fill-orange-500/50"
              : "text-gray-500"
          }`}
        />
      ))}
    </div>
  );
}

function StatItem({ label, value }) {
  return (
    <div className="flex justify-between items-center py-2 border-b border-gray-600 last:border-0">
      <span className="text-gray-300 text-sm">{label}</span>
      <div className="flex items-center gap-2">
        <StarDisplay rating={value} size="h-3 w-3" />
        <span className="text-white font-medium text-sm">{value.toFixed(1)}</span>
      </div>
    </div>
  );
}

export default function ReviewSection() {
  const { t } = useTranslation();
  const { getPath } = useLocalizedPath();
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const carouselRef = useRef(null);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [reviewsRes, statsRes] = await Promise.all([
          fetch(`${API_URL}/api/reviews`),
          fetch(`${API_URL}/api/reviews/stats`)
        ]);
        
        const reviewsData = await reviewsRes.json();
        const statsData = await statsRes.json();
        
        setReviews(reviewsData);
        setStats(statsData);
      } catch (error) {
        console.error("Failed to fetch reviews:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  const nextSlide = () => {
    if (reviews.length > 0) {
      setCurrentIndex((prev) => (prev + 1) % reviews.length);
    }
  };
  
  const prevSlide = () => {
    if (reviews.length > 0) {
      setCurrentIndex((prev) => (prev - 1 + reviews.length) % reviews.length);
    }
  };
  
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };
  
  if (loading) {
    return (
      <section className="py-16 md:py-24 bg-[#1e2a3a]" data-testid="reviews-section-loading">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl text-center">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-700 rounded w-64 mx-auto mb-4"></div>
            <div className="h-4 bg-gray-700 rounded w-48 mx-auto"></div>
          </div>
        </div>
      </section>
    );
  }
  
  const hasReviews = stats && stats.totalReviews > 0;
  
  return (
    <section className="py-16 md:py-24 bg-[#1e2a3a]" data-testid="reviews-section">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {t('reviews.section.title')}
          </h2>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            {t('reviews.section.subtitle')}
          </p>
        </div>
        
        {hasReviews ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Stats Card */}
            <Card className="bg-[#2d3a4a] border-0 lg:col-span-1">
              <CardContent className="p-6">
                {/* Overall Rating */}
                <div className="text-center mb-6 pb-6 border-b border-gray-600">
                  <div className="text-5xl font-bold text-white mb-2">
                    {stats.averageRating.toFixed(1)}
                  </div>
                  <StarDisplay rating={stats.averageRating} size="h-6 w-6" />
                  <p className="text-gray-400 mt-2">
                    {t('reviews.section.basedOn', { count: stats.totalReviews })}
                  </p>
                  <p className="text-green-400 text-sm mt-1">
                    {stats.recommendationRate}% {t('reviews.section.recommend')}
                  </p>
                </div>
                
                {/* Detailed Ratings */}
                <div className="space-y-1">
                  <StatItem label={t('reviews.form.ratings.cleanliness')} value={stats.averageCleanliness} />
                  <StatItem label={t('reviews.form.ratings.worksiteOrder')} value={stats.averageWorksiteOrder} />
                  <StatItem label={t('reviews.form.ratings.quality')} value={stats.averageQuality} />
                  <StatItem label={t('reviews.form.ratings.value')} value={stats.averageValue} />
                  <StatItem label={t('reviews.form.ratings.transparency')} value={stats.averageTransparency} />
                  <StatItem label={t('reviews.form.ratings.communication')} value={stats.averageCommunication} />
                </div>
              </CardContent>
            </Card>
            
            {/* Reviews Carousel */}
            <div className="lg:col-span-2">
              {reviews.length > 0 && (
                <div className="relative">
                  {/* Carousel Container */}
                  <div className="overflow-hidden" ref={carouselRef}>
                    <div
                      className="flex transition-transform duration-300 ease-in-out"
                      style={{ transform: `translateX(-${currentIndex * 100}%)` }}
                    >
                      {reviews.map((review) => (
                        <div key={review.id} className="w-full flex-shrink-0 px-2">
                          <Card className="bg-[#2d3a4a] border-0 h-full">
                            <CardContent className="p-6">
                              <div className="flex items-start gap-4 mb-4">
                                <div className="w-12 h-12 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold text-xl">
                                  {review.firstName.charAt(0).toUpperCase()}
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="text-white font-semibold">
                                      {review.firstName}
                                    </span>
                                    {review.verified && (
                                      <span className="inline-flex items-center gap-1 text-xs text-green-400 bg-green-400/10 px-2 py-0.5 rounded-full">
                                        <CheckCircle2 className="h-3 w-3" />
                                        {t('reviews.section.verified')}
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-gray-400 text-sm">
                                    {review.city} • {formatDate(review.createdAt)}
                                  </div>
                                  {review.projectType && (
                                    <div className="text-orange-400 text-xs mt-1">
                                      {t(`reviews.form.projectTypes.${review.projectType}`, { defaultValue: review.projectType })}
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              <div className="mb-4">
                                <StarDisplay rating={review.overallRating} size="h-5 w-5" />
                              </div>
                              
                              <div className="relative">
                                <Quote className="absolute -top-2 -left-2 h-8 w-8 text-orange-500/20" />
                                <p className="text-gray-300 leading-relaxed pl-4">
                                  {review.comment.length > 300
                                    ? review.comment.substring(0, 300) + "..."
                                    : review.comment}
                                </p>
                              </div>
                            </CardContent>
                          </Card>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Navigation Buttons */}
                  {reviews.length > 1 && (
                    <>
                      <button
                        onClick={prevSlide}
                        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-10 h-10 rounded-full bg-orange-500 text-white flex items-center justify-center hover:bg-orange-600 transition-colors shadow-lg"
                        aria-label="Previous review"
                      >
                        <ChevronLeft className="h-6 w-6" />
                      </button>
                      <button
                        onClick={nextSlide}
                        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-10 h-10 rounded-full bg-orange-500 text-white flex items-center justify-center hover:bg-orange-600 transition-colors shadow-lg"
                        aria-label="Next review"
                      >
                        <ChevronRight className="h-6 w-6" />
                      </button>
                    </>
                  )}
                  
                  {/* Dots Indicator */}
                  {reviews.length > 1 && (
                    <div className="flex justify-center gap-2 mt-6">
                      {reviews.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentIndex(index)}
                          className={`w-2 h-2 rounded-full transition-colors ${
                            index === currentIndex ? "bg-orange-500" : "bg-gray-600"
                          }`}
                          aria-label={`Go to review ${index + 1}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          /* No Reviews Yet */
          <div className="text-center py-12">
            <div className="text-gray-400 text-lg mb-6">
              {t('reviews.section.noReviews')}
            </div>
          </div>
        )}
        
        {/* CTA Button */}
        <div className="text-center mt-12">
          <Button
            asChild
            size="lg"
            className="bg-orange-500 hover:bg-orange-600 text-white text-lg h-14 px-8"
            data-testid="write-review-btn"
          >
            <Link to={getPath('review')}>
              <Star className="mr-2 h-5 w-5" />
              {t('reviews.section.writeReview')}
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
